import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, EmptyState, Field, Modal, MultiSearchSelect, Notice, PageHeader, SearchSelect, Select, Table, Tag, TextArea, type SearchOption } from '@voice/ui';
import { errorText, formatDateTime, useDirectory } from '../components/common';
import { ContextMenu, type MenuItem } from '../components/ContextMenu';
import { FlowCanvas, autoLayout } from '../components/FlowCanvas';
import { ParamEditor } from '../components/ParamEditor';
import { SpeechField, type Speech } from '../components/SpeechField';
import { ApiError, api } from '../lib/api';
import styles from './Pages.module.css';

interface FlowListItem {
  id: string;
  name: string;
  number: string | null;
  description: string;
  updatedAt: string;
}
interface NodeData {
  id: string;
  type: string;
  params: Record<string, unknown>;
  next: Record<string, string>;
  pos?: { x: number; y: number };
}
interface Graph {
  start: string;
  nodes: Record<string, NodeData>;
}
interface FlowFull extends FlowListItem {
  graph: Graph;
}
interface NodeType {
  type: string;
  label: string;
  category: string;
  description: string;
  defaults: Record<string, unknown>;
  ports: string[];
  portLabels: Record<string, string>;
}
interface Problems {
  errors: string[];
  warnings: string[];
}

// ---------------------------------------------------------------- Liste

export function FlowList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['flows'], queryFn: () => api<{ flows: FlowListItem[] }>('/flows') });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api<{ id: string }>('/flows', { method: 'POST', body: { name } }),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['flows'] });
      await qc.invalidateQueries({ queryKey: ['directory'] });
      navigate(`/ablaeufe/${r.id}`);
    },
  });
  return (
    <div className={styles.page}>
      <PageHeader title="Abläufe und Sekretariat">
        <Button onClick={() => setCreating(true)}>Neuer Ablauf</Button>
      </PageHeader>
      <p className={styles.note} style={{ marginBottom: 20 }}>
        Ein Ablauf beschreibt, was Anrufer hören und eingeben: Ansagen, Menüs, Fragen mit Tastatur oder Sprache, Geschäftszeiten, Weiterleitung, Nachricht hinterlassen. Rufnummern weisen Anrufe einem Ablauf zu (Leitungen).
      </p>
      {query.isError && <Notice tone="error">Die Abläufe konnten nicht geladen werden.</Notice>}
      {query.data?.flows.length === 0 && <EmptyState>Noch kein Ablauf.</EmptyState>}
      {query.data && query.data.flows.length > 0 && (
        <Table aria-label="Abläufe">
          <thead>
            <tr>
              <th>Name</th>
              <th>Testnummer</th>
              <th>Geändert</th>
            </tr>
          </thead>
          <tbody>
            {query.data.flows.map((f) => (
              <tr key={f.id}>
                <th scope="row">
                  <Link to={`/ablaeufe/${f.id}`}>{f.name}</Link>
                  {f.description && <div className={styles.muted}>{f.description}</div>}
                </th>
                <td>{f.number ?? <span className={styles.muted}>–</span>}</td>
                <td>{formatDateTime(f.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {creating && (
        <Modal
          open
          onClose={() => setCreating(false)}
          title="Neuer Ablauf"
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setCreating(false)}>
                Abbrechen
              </Button>
              <Button onClick={() => create.mutate()} disabled={!name || create.isPending}>
                Anlegen
              </Button>
            </>
          }
        >
          <div className={styles.form}>
            <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sekretariat" autoFocus />
            {create.isError && <Notice tone="error">{errorText(create.error)}</Notice>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Parameter-Editor

// ---------------------------------------------------------------- Editor

export function FlowEditor() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const flow = useQuery({ queryKey: ['flow', id], queryFn: () => api<FlowFull>(`/flows/${id}`) });
  const types = useQuery({ queryKey: ['flow-types'], queryFn: () => api<{ types: NodeType[] }>('/flows/node-types'), staleTime: Infinity });
  const [graph, setGraph] = useState<Graph | null>(null);
  const [meta, setMeta] = useState({ name: '', description: '', number: '' });
  const [open, setOpen] = useState<string | null>(null);
  const [problems, setProblems] = useState<Problems | null>(null);
  const [adding, setAdding] = useState(false);
  const [mode, setMode] = useState<'graph' | 'list'>('graph');
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);
  const [dialog, setDialog] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [portCache, setPortCache] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (flow.data && !graph) {
      const g = flow.data.graph;
      const spots = Object.values(g.nodes).map((n) => (n.pos ? `${n.pos.x}:${n.pos.y}` : ''));
      const needs = spots.some((x) => x === '') || new Set(spots).size < spots.length;
      if (needs) {
        const layout = autoLayout(g);
        setGraph({ ...g, nodes: Object.fromEntries(Object.entries(g.nodes).map(([k, n]) => [k, { ...n, pos: layout[k]! }])) });
      } else setGraph(g);
      setMeta({ name: flow.data.name, description: flow.data.description, number: flow.data.number ?? '' });
      setOpen(flow.data.graph.start);
    }
  }, [flow.data, graph]);

  const typeMap = useMemo(() => new Map((types.data?.types ?? []).map((t) => [t.type, t])), [types.data]);

  // Ausgänge hängen von den Einstellungen ab (z. B. Tasten eines Menüs) – der Server rechnet sie aus.
  const nodeKey = graph ? JSON.stringify(Object.values(graph.nodes).map((n) => [n.id, n.type, n.params])) : '';
  useEffect(() => {
    if (!graph) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const next: Record<string, string[]> = {};
      await Promise.all(
        Object.values(graph.nodes).map(async (n) => {
          try {
            next[n.id] = (await api<{ ports: string[] }>('/flows/node-ports', { method: 'POST', body: { type: n.type, params: n.params } })).ports;
          } catch {
            next[n.id] = typeMap.get(n.type)?.ports ?? ['default'];
          }
        }),
      );
      if (!cancelled) setPortCache(next);
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey, typeMap]);

  const update = (fn: (g: Graph) => Graph) => {
    setGraph((g) => (g ? fn(g) : g));
    setDirty(true);
  };
  const patchNode = (nid: string, patch: Partial<NodeData>) => update((g) => ({ ...g, nodes: { ...g.nodes, [nid]: { ...g.nodes[nid]!, ...patch } } }));

  const validate = useMutation({ mutationFn: () => api<Problems>('/flows/validate', { method: 'POST', body: { graph } }), onSuccess: setProblems });
  const save = useMutation({
    mutationFn: () => api(`/flows/${id}`, { method: 'PUT', body: { name: meta.name, description: meta.description, number: meta.number || null, graph } }),
    onSuccess: async () => {
      setDirty(false);
      setProblems(null);
      await qc.invalidateQueries({ queryKey: ['flows'] });
      await qc.invalidateQueries({ queryKey: ['directory'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) setProblems({ errors: [err.message], warnings: [] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/flows/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['flows'] });
      navigate('/ablaeufe');
    },
  });

  if (flow.isError) return <Notice tone="error">Der Ablauf konnte nicht geladen werden.</Notice>;
  if (!graph) return <p className={styles.note}>Lädt …</p>;

  const ids = Object.keys(graph.nodes);
  const ordered = [graph.start, ...ids.filter((x) => x !== graph.start)];
  const labelOf = (nid: string) => `${graph.nodes[nid]?.id} · ${typeMap.get(graph.nodes[nid]?.type ?? '')?.label ?? graph.nodes[nid]?.type}`;

  const freeId = (base: string) => {
    let n = 1;
    while (graph.nodes[`${base}${n}`]) n++;
    return `${base}${n}`;
  };
  const addNode = (type: NodeType, at?: { x: number; y: number }, between?: { from: string; port: string; to: string }) => {
    const nid = freeId(type.type);
    update((g) => {
      const nodes: Record<string, NodeData> = { ...g.nodes, [nid]: { id: nid, type: type.type, params: structuredClone(type.defaults), next: (between ? { default: between.to } : {}) as Record<string, string>, pos: at ?? { x: 0, y: ids.length * 140 } } };
      if (between) nodes[between.from] = { ...nodes[between.from]!, next: { ...nodes[between.from]!.next, [between.port]: nid } };
      return { ...g, nodes };
    });
    setOpen(nid);
    setAdding(false);
    if (mode === 'graph') setDialog(nid);
  };
  const duplicate = (from: string) => {
    const src = graph.nodes[from]!;
    const nid = freeId(src.type);
    update((g) => ({ ...g, nodes: { ...g.nodes, [nid]: { id: nid, type: src.type, params: structuredClone(src.params), next: {}, pos: { x: (src.pos?.x ?? 0) + 30, y: (src.pos?.y ?? 0) + 40 } } } }));
    setOpen(nid);
  };
  const typeMenu = (pick: (t: NodeType) => void): MenuItem[] => {
    const cats = new Map<string, NodeType[]>();
    for (const t of types.data?.types ?? []) cats.set(t.category, [...(cats.get(t.category) ?? []), t]);
    return [...cats.entries()].map(([label, list]) => ({ label, children: list.map((t) => ({ label: t.label, onClick: () => pick(t) })) }));
  };
  const openMenu = (info: Parameters<React.ComponentProps<typeof FlowCanvas>['onContext']>[0]) => {
    if (info.kind === 'canvas') {
      setMenu({
        x: info.x,
        y: info.y,
        items: [{ heading: 'Baustein hinzufügen' }, ...typeMenu((t) => addNode(t, { x: Math.round(info.world.x), y: Math.round(info.world.y) })), { separator: true }, { label: 'Alle anordnen', onClick: arrange }],
      });
    } else if (info.kind === 'node') {
      const nid = info.id;
      setMenu({
        x: info.x,
        y: info.y,
        items: [
          { label: 'Bearbeiten …', hint: 'Doppelklick', onClick: () => setDialog(nid) },
          { label: 'Als Start setzen', disabled: nid === graph.start, onClick: () => update((g) => ({ ...g, start: nid })) },
          { label: 'Kennung ändern …', onClick: () => { const to = prompt('Neue Kennung (Buchstaben, Ziffern, Unterstrich)', nid)?.trim(); if (to && to !== nid) rename(nid, to); } },
          { label: 'Duplizieren', onClick: () => duplicate(nid) },
          { separator: true },
          { label: 'Löschen', danger: true, disabled: ids.length <= 1, onClick: () => removeNode(nid) },
        ],
      });
    } else {
      const to = graph.nodes[info.id]?.next[info.port];
      setMenu({
        x: info.x,
        y: info.y,
        items: [
          { label: 'Verbindung löschen', danger: true, onClick: () => connect(info.id, info.port, null) },
          { separator: true },
          { heading: 'Baustein dazwischen einfügen' },
          ...(to ? typeMenu((t) => addNode(t, { x: Math.round(((graph.nodes[info.id]?.pos?.x ?? 0) + (graph.nodes[to]?.pos?.x ?? 0)) / 2), y: Math.round(((graph.nodes[info.id]?.pos?.y ?? 0) + (graph.nodes[to]?.pos?.y ?? 0)) / 2 + 40) }, { from: info.id, port: info.port, to })) : []),
        ],
      });
    }
  };
  const removeNode = (nid: string) =>
    update((g) => {
      const nodes = { ...g.nodes };
      delete nodes[nid];
      for (const n of Object.values(nodes)) n.next = Object.fromEntries(Object.entries(n.next).filter(([, t]) => t !== nid));
      return { start: g.start === nid ? (Object.keys(nodes)[0] ?? '') : g.start, nodes };
    });
  const rename = (from: string, to: string) => {
    if (!/^[\p{L}_][\p{L}\p{N}_]{0,40}$/u.test(to) || graph.nodes[to]) return;
    update((g) => {
      const nodes: Record<string, NodeData> = {};
      for (const [k, n] of Object.entries(g.nodes)) {
        const copy = { ...n, id: k === from ? to : n.id, next: Object.fromEntries(Object.entries(n.next).map(([p, t]) => [p, t === from ? to : t])) };
        nodes[k === from ? to : k] = copy;
      }
      return { start: g.start === from ? to : g.start, nodes };
    });
    setOpen(to);
    setDialog((d) => (d === from ? to : d));
  };

  const renderBody = (nid: string) => {
    const node = graph.nodes[nid]!;
    const spec = typeMap.get(node.type);
    const ports = portCache[nid] ?? spec?.ports ?? ['default'];
    return (
            <div className={styles.cardBody}>
              {spec && <p className={styles.muted}>{spec.description}</p>}
              <ParamEditor type={node.type} params={node.params} onChange={(p) => patchNode(nid, { params: p })} />
              <div>
                <h3 className={styles.heading}>Wie geht es weiter?</h3>
                <div className={styles.row}>
                  {ports.map((p) => (
                    <Select
                      key={p}
                      label={spec?.portLabels[p] ?? (p === 'default' ? 'Danach' : p)}
                      value={node.next[p] ?? ''}
                      onChange={(e) => {
                        const next = { ...node.next };
                        if (e.target.value) next[p] = e.target.value;
                        else delete next[p];
                        patchNode(nid, { next });
                      }}
                    >
                      <option value="">Gespräch beenden</option>
                      {ids.map((x) => (
                        <option key={x} value={x}>
                          {labelOf(x)}
                        </option>
                      ))}
                    </Select>
                  ))}
                </div>
              </div>
              <div className={styles.inline}>
                {nid !== graph.start && (
                  <Button variant="secondary" onClick={() => update((g) => ({ ...g, start: nid }))}>
                    <ArrowUp size={14} /> Als Start setzen
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => {
                    const to = prompt('Neue Kennung (Buchstaben, Ziffern, Unterstrich)', nid)?.trim();
                    if (to && to !== nid) rename(nid, to);
                  }}
                >
                  Kennung ändern
                </Button>
                <Button variant="secondary" onClick={() => confirm(`Baustein „${nid}“ löschen?`) && removeNode(nid)} disabled={ids.length <= 1}>
                  Löschen
                </Button>
              </div>
            </div>
    );
  };

  const setPos = (nid: string, pos: { x: number; y: number }) => update((g) => ({ ...g, nodes: { ...g.nodes, [nid]: { ...g.nodes[nid]!, pos } } }));
  const connect = (nid: string, port: string, target: string | null) =>
    update((g) => {
      const next = { ...g.nodes[nid]!.next };
      if (target) next[port] = target;
      else delete next[port];
      return { ...g, nodes: { ...g.nodes, [nid]: { ...g.nodes[nid]!, next } } };
    });
  const arrange = () =>
    update((g) => {
      const layout = autoLayout(g);
      return { ...g, nodes: Object.fromEntries(Object.entries(g.nodes).map(([k, n]) => [k, { ...n, pos: layout[k]! }])) };
    });

  const byCategory = new Map<string, NodeType[]>();
  for (const t of types.data?.types ?? []) byCategory.set(t.category, [...(byCategory.get(t.category) ?? []), t]);

  return (
    <div className={styles.page}>
      <PageHeader title={meta.name || 'Ablauf'}>
        <Link to="/ablaeufe">Alle Abläufe</Link>
        <Button variant="secondary" onClick={() => validate.mutate()}>
          Prüfen
        </Button>
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {dirty ? 'Speichern' : 'Gespeichert'}
        </Button>
      </PageHeader>

      {problems && (
        <div className={styles.stack} style={{ marginBottom: 16 }}>
          {problems.errors.map((e) => (
            <Notice key={e} tone="error">
              {e}
            </Notice>
          ))}
          {problems.warnings.map((w) => (
            <Notice key={w}>{w}</Notice>
          ))}
          {problems.errors.length === 0 && problems.warnings.length === 0 && <Notice tone="success">Der Ablauf ist in Ordnung.</Notice>}
        </div>
      )}

      <div className={styles.row} style={{ marginBottom: 24 }}>
        <Field label="Name" value={meta.name} onChange={(e) => { setMeta({ ...meta, name: e.target.value }); setDirty(true); }} />
        <Field label="Testnummer (intern, optional)" inputMode="numeric" value={meta.number} onChange={(e) => { setMeta({ ...meta, number: e.target.value.replace(/\D/g, '') }); setDirty(true); }} hint="Intern wählbar, um den Ablauf auszuprobieren." />
      </div>
      <TextArea label="Beschreibung" value={meta.description} rows={2} onChange={(e) => { setMeta({ ...meta, description: e.target.value }); setDirty(true); }} style={{ marginBottom: 24 }} />

      <div className={styles.inline} style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 className={styles.heading} style={{ margin: 0 }}>
          Bausteine
        </h2>
        <div className={styles.inline}>
          <Button variant={mode === 'graph' ? 'primary' : 'secondary'} onClick={() => setMode('graph')} aria-pressed={mode === 'graph'}>
            Grafik
          </Button>
          <Button variant={mode === 'list' ? 'primary' : 'secondary'} onClick={() => setMode('list')} aria-pressed={mode === 'list'}>
            Liste
          </Button>
          {mode === 'graph' && (
            <Button variant="secondary" onClick={arrange}>
              Anordnen
            </Button>
          )}
          {mode === 'list' && (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              Baustein hinzufügen
            </Button>
          )}
        </div>
      </div>

      {mode === 'graph' && (
        <div className={styles.stack} style={{ marginBottom: 24 }}>
          <FlowCanvas graph={graph} types={typeMap} ports={portCache} selected={open} onSelect={setOpen} onMove={setPos} onConnect={connect} onContext={openMenu} onOpen={setDialog} />
          <p className={styles.muted}>Rechtsklick auf die Fläche: Baustein hinzufügen · Rechtsklick auf einen Baustein oder eine Linie: Menü · Doppelklick auf einen Baustein: bearbeiten · Am Punkt rechts ziehen und auf einem Baustein loslassen: verbinden.</p>
        </div>
      )}
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} />}
      {dialog && graph.nodes[dialog] && (
        <Modal open onClose={() => setDialog(null)} title={typeMap.get(graph.nodes[dialog]!.type)?.label ?? graph.nodes[dialog]!.type} description={`Kennung: ${dialog}`} size="lg" footer={<Button onClick={() => setDialog(null)}>Fertig</Button>}>
          {renderBody(dialog)}
        </Modal>
      )}

      <div className={styles.stack} style={mode === 'graph' ? { display: 'none' } : undefined}>
        {ordered.map((nid) => {
          const node = graph.nodes[nid]!;
          const spec = typeMap.get(node.type);
          const ports = portCache[nid] ?? spec?.ports ?? ['default'];
          const isOpen = open === nid;
          return (
            <div key={nid} className={styles.card}>
              <button type="button" className={styles.cardHead} onClick={() => setOpen(isOpen ? null : nid)} aria-expanded={isOpen}>
                <span>
                  <strong>{spec?.label ?? node.type}</strong> <span className={styles.mono}>{nid}</span>
                  {nid === graph.start && <Tag tone="ok">Start</Tag>}
                </span>
                <span className={styles.muted}>
                  {ports.map((p) => (node.next[p] ? `${spec?.portLabels[p] ?? p} → ${node.next[p]}` : null)).filter(Boolean).join(' · ') || 'kein Weiterführen'}
                </span>
              </button>
              {isOpen && renderBody(nid)}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 32 }}>
        <Button variant="secondary" onClick={() => confirm('Ablauf löschen? Rufnummern, die ihn nutzen, laufen danach ins Leere.') && remove.mutate()}>
          Ablauf löschen
        </Button>
        {remove.isError && <Notice tone="error">{errorText(remove.error)}</Notice>}
      </div>

      {adding && (
        <Modal open onClose={() => setAdding(false)} title="Baustein hinzufügen" size="lg">
          <div className={styles.stack}>
            {[...byCategory.entries()].map(([cat, list]) => (
              <div key={cat}>
                <h3 className={styles.heading}>{cat}</h3>
                <div className={styles.deviceList}>
                  {list.map((t) => (
                    <div key={t.type} className={styles.device}>
                      <div className={styles.deviceInfo}>
                        <span className={styles.deviceName}>{t.label}</span>
                        <span className={styles.muted}>{t.description}</span>
                      </div>
                      <Button variant="secondary" onClick={() => addNode(t)}>
                        Hinzufügen
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
